import { Avatar, Button, Listy, Tooltip } from 'antd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { WebSocketEventTypeEnum } from '../../../enums/WebSocketEventTypeEnum';
import type File from '../../../interfaces/File';
import type Notification from '../../../interfaces/Notification';
import { resolveNotificationFileRef } from '../../../lib/notifications/resolveAiEventFileRef';
import {
  resolveNotificationDescription,
  resolveNotificationTitle,
} from '../../../lib/notifications/resolveNotificationCopy';
import { Icon } from '../../Core/Icon';
import { FILE_PREVIEW_EXTENSIONS_IMAGES } from '../../../utils/utils';
import FileIcon from '../FileIcon';
import ListyMetaRow from '../ListyMetaRow';
import { LISTY_ITEM_NO_HOVER } from '../listyItemClassNames';
import TruncatedTooltip from '../TruncatedTooltip';

export interface NotificationsListProps {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  formatTimestamp: (date: string) => string;
  getNotificationIcon: (
    eventType: string,
  ) => 'TagIcon' | 'FilesIcon' | 'AiFileIcon' | 'ShareIcon' | 'BellIcon';
  openFileSafely: (file: File) => void;
  redirectFileToLocation: (params: { file: File }) => void;
  /** Opens the AI summary modal; used by "summary" notifications instead of opening the file. */
  openAiSummary?: (file: File) => void;
}

/**
 * List of notifications with header actions (mark all read, clear all) and scrollable content.
 * Used inside the Header notifications Popover.
 */
export default function NotificationsList({
  notifications,
  unreadCount,
  markAsRead,
  markAllAsRead,
  clearAll,
  formatTimestamp,
  getNotificationIcon,
  openFileSafely,
  redirectFileToLocation,
  openAiSummary,
}: NotificationsListProps) {
  const { t } = useTranslation();
  const hasAssociatedFileId = (notification: Notification): boolean =>
    resolveNotificationFileRef(notification) != null;

  /** Whether clicking the notification's file should open the AI summary instead of the file. */
  const opensAiSummary = (notification: Notification): boolean =>
    Boolean(openAiSummary) &&
    notification.eventType === WebSocketEventTypeEnum.Summary &&
    Boolean(notification.file) &&
    !notification.file?.isFolder;

  return (
    <div
      className="flex max-h-[70vh] w-80 flex-col"
      role="region"
      aria-label={t('a11y.notificationsList')}
    >
      <div className="border-gray-medium mb-3 shrink-0 border-b pb-2">
        <div className="flex items-center justify-between">
          <h3 className="text-blue-dark text-lg font-semibold">{t('common.notifications')}</h3>
          {notifications.length > 0 && (
            <div className="flex gap-1">
              <Tooltip title={t('common.markAllRead')}>
                <Button
                  size="small"
                  aria-label={t('a11y.markAllRead')}
                  onClick={markAllAsRead}
                  disabled={unreadCount === 0}
                >
                  <Icon name="ListChecksIcon" className="h-4 w-4" />
                </Button>
              </Tooltip>
              <Tooltip title={t('common.clearAll')}>
                <Button
                  size="small"
                  aria-label={t('a11y.clearAllNotifications')}
                  onClick={clearAll}
                  danger
                >
                  <Icon name="CircleXIcon" className="h-4 w-4" />
                </Button>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      {notifications.length === 0 ? (
        <div className="text-gray-dark py-8 text-center" role="status">
          {t('common.noNotifications')}
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <Listy
            items={notifications}
            rowKey="id"
            classNames={{ item: clsx(LISTY_ITEM_NO_HOVER, 'p-0!') }}
            itemRender={(notification) => (
              <div
                className={clsx('rounded-md px-0 py-3 transition-colors', {
                  'hover:bg-gray-light cursor-pointer': !notification.isRead,
                })}
                {...(!notification.isRead
                  ? {
                      role: 'button' as const,
                      tabIndex: 0,
                      'aria-label': t('common.markAsRead'),
                      onClick: () => markAsRead(notification.id),
                      onKeyDown: (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          markAsRead(notification.id);
                        }
                      },
                    }
                  : {})}
              >
                <ListyMetaRow
                  className="py-0"
                  avatar={
                    <Avatar
                      icon={
                        notification.isLoadingFile ? (
                          <Icon name="DownloadIcon" className="h-5 w-5 animate-pulse" />
                        ) : (
                          <Icon
                            name={getNotificationIcon(notification.eventType)}
                            className="h-5 w-5"
                          />
                        )
                      }
                      shape="square"
                      className={clsx(
                        'text-blue-dark!',
                        notification.isLoadingFile
                          ? 'bg-blue-dark/20! animate-pulse!'
                          : !notification.isRead
                            ? 'bg-blue-dark/20!'
                            : 'bg-gray-light!',
                      )}
                    />
                  }
                  title={
                    <div className="flex items-start justify-between">
                      <span
                        className={clsx(
                          'text-sm font-medium',
                          !notification.isRead ? 'text-blue-dark font-semibold' : 'text-gray-dark',
                        )}
                      >
                        {resolveNotificationTitle(t, notification)}
                      </span>
                      <span className="text-gray-dark ml-2 text-xs">
                        {formatTimestamp(notification.createdAt)}
                      </span>
                    </div>
                  }
                  description={
                    <div className="space-y-1">
                      <span className="text-gray-dark text-sm">
                        {resolveNotificationDescription(t, notification)}
                      </span>
                      {hasAssociatedFileId(notification) && (
                        <div className="text-blue-dark text-xs">
                          {notification.isLoadingFile ? (
                            <span className="flex items-center gap-1">
                              <Icon name="DownloadIcon" className="h-3 w-3 animate-pulse" />
                              {t('common.loading')}...
                            </span>
                          ) : notification.file ? (
                            <Tooltip
                              title={
                                opensAiSummary(notification)
                                  ? t('notifications.summary.open')
                                  : t('common.open', {
                                      fileName: notification.file.fileName,
                                    })
                              }
                            >
                              <Button
                                className="text-blue-dark! -ml-2.5! flex! max-w-[220px]! flex-row! items-center! gap-1! text-xs!"
                                size="small"
                                type="text"
                                disabled={!notification.file}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!notification.file) return;
                                  if (!notification.isRead) markAsRead(notification.id);
                                  if (opensAiSummary(notification)) {
                                    openAiSummary?.(notification.file);
                                    return;
                                  }
                                  if (notification.file.isFolder) {
                                    redirectFileToLocation({ file: notification.file });
                                    return;
                                  }
                                  openFileSafely(notification.file);
                                }}
                              >
                                <FileIcon
                                  fileId={notification?.file?.id}
                                  fileExtension={notification.file.fileExtension}
                                  isFolder={notification.file.isFolder}
                                  allowedFilePreviewExtensions={FILE_PREVIEW_EXTENSIONS_IMAGES}
                                  className="h-4 w-4 shrink-0"
                                />
                                <TruncatedTooltip>{notification.file.fileName}</TruncatedTooltip>
                              </Button>
                            </Tooltip>
                          ) : notification.fileEnrichFailed ? (
                            <span className="text-gray-dark">
                              {t('notifications.fileUnavailable')}
                            </span>
                          ) : (
                            <span className="text-gray-dark flex items-center gap-1">
                              <Icon name="ClockArrowDownIcon" className="h-3 w-3 animate-pulse" />
                              {t('common.waiting') || 'Waiting...'}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  }
                />
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
}
